import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { map, first } from 'rxjs/operators';

import { DropdownItem } from '@shared';

import { ProblemInstanceService } from '../problem-instance/problem-instance.service';
import { SkillRequirement, TaskView } from '../../models';

@Injectable({
    providedIn: 'root'
})
export class SkillLevelService {
    private readonly problemInstanceService = inject(ProblemInstanceService);


    public getSkillLevelDropdownItems(): Observable<DropdownItem[]> {
        return this.problemInstanceService.getProblemInstance().pipe(
            map(result =>
                result.problemInstance.skillLevels.map(
                    skillLevel =>
                        <DropdownItem>{
                            value: skillLevel.skillLevelId,
                            label: `${skillLevel.skillLevelId} ${skillLevel.skillLevelDescription}`
                        }
                )
            )
        );
    }

    public getSkillLevelDescription(skillLevelId: number): Observable<string> {
        return this.problemInstanceService.getProblemInstance().pipe(
            first(),
            map(
                result =>
                    result.problemInstance.skillLevels.find(skillLevel => skillLevel.skillLevelId === skillLevelId)
                        ?.skillLevelDescription
            )
        );
    }

    public getSkillRequirementDescription(task: TaskView, skillLevels: SkillRequirement[]): string {
        if (!skillLevels || skillLevels.length === 0) {
            return '';
        }

        skillLevels.sort((a, b) => a.skillLevelId - b.skillLevelId);

        const skillLevelDescription = this.getMatchingSkillLevelDescription(skillLevels, task.skillRequirement);
        const higherSkillLevelsAcceptedDescription = this.getHigherLevelsDescription(skillLevels, task.skillRequirement, task.numberOfHigherSkillLevelsAccepted);

        return `${skillLevelDescription} - ${higherSkillLevelsAcceptedDescription}`;
    }

    private getMatchingSkillLevelDescription(skillLevels: SkillRequirement[], skillRequirement: number): string {
        const skillLevel = skillLevels.find(item => item.skillLevelId === skillRequirement);
        return skillLevel ? skillLevel.skillLevelDescription : skillLevels[0].skillLevelDescription;
    }

    private getHigherLevelsDescription(skillLevels: SkillRequirement[], skillRequirement: number, numberOfHigherSkillLevelsAccepted: number): string {
        const highestSkillLevel = skillLevels[skillLevels.length - 1];
        const higherSkillLevelsAccepted = skillRequirement + numberOfHigherSkillLevelsAccepted;
        if (higherSkillLevelsAccepted < highestSkillLevel.skillLevelId) {
            const higherLevel = skillLevels.find(item => item.skillLevelId >= higherSkillLevelsAccepted);
            return higherLevel ? higherLevel.skillLevelDescription : highestSkillLevel.skillLevelDescription;
        }
        return highestSkillLevel.skillLevelDescription;
    }

    public getAvailableSkillRequirementDropdownItems(unassignedTask: TaskView): Observable<DropdownItem[]> {
        if (!unassignedTask.originalSkillId) {
            unassignedTask.originalSkillId = unassignedTask.skillRequirement;
        }
        return this
            .problemInstanceService
            .getProblemInstance()
            .pipe(map(result => {
                let skillsSorted: SkillRequirement[] = result
                    .problemInstance.skillLevels
                    .sort((a, b) => a.skillLevelId - b.skillLevelId);
                let higherSkillIndex = skillsSorted.find(element => element.skillLevelId === unassignedTask.originalSkillId).skillLevelId;
                if (higherSkillIndex > skillsSorted.length) { higherSkillIndex = skillsSorted.length; }
                let skillsSliced = skillsSorted.slice(0, higherSkillIndex );
                return skillsSliced.map(skillRequirement =>
                     <DropdownItem>{
                            value: skillRequirement.skillLevelId,
                            label: `${this.getMatchingSkillLevelDescription(skillsSorted, skillRequirement.skillLevelId)} - ${this.getHigherLevelsDescription(skillsSorted, skillRequirement.skillLevelId, unassignedTask.numberOfHigherSkillLevelsAccepted)}`
                        }
                    );
            }));
    }
}
